import { Controller } from "@hotwired/stimulus"
import { get_csrf_token } from '../app/csrf_token.js'

// Connects to data-controller="content-editable-form"
//
// The big idea for this controller is to serialize and submit contenteditable elements using
// associated input-name and input-attributes.
//
// This controller supports two different ways for usage:
//
//		- using formTarget
//			This way will append inputs as HTML elements to the end of the formTarget, submit
//			the form, then remove all elements that match the input name attributes of serialized inputs.
//
//		- using tableId
//			This way will create it's own fetch API request and add inputs as JSON objects to the request.
//			A turbo-stream response is required and handled automatically.
export default class extends Controller {

	static targets = [
		'form',
		'table',
	]

	// tableIdValue can be used as alternate specification to the tableTarget.
	// This is necessary for pages with multiple forms that the stimulus controller
	// may not be able to be a parent of the table.
	static values = { tableId: String }

	connect() {
		if (!this.hasTableTarget && !this.hasTableIdValue) {
			console.error('Missing tableTarget for ContentEditableFormController.')
			return
		}

		this.table = this.hasTableTarget ? this.tableTarget : document.getElementById(this.tableIdValue)

		if (this.hasFormTarget) {
			this.formTarget.addEventListener('submit', function(event) {
				event.preventDefault()
				const contentEditableFormController = Stimulus.getControllerForElementAndIdentifier(event.currentTarget, 'content-editable-form')
				contentEditableFormController.submitForm()
			})
		}
	}

	// This method should serialize all untracked and dirty elements who have the data-input-name attribute defined.
	serialize() {
		const inputElements = Array.from(this.table.querySelectorAll('[data-input-name]'))
			.filter(input => input.dataset.elementIsDirty === undefined || input.dataset.elementIsDirty === 'true')

		const inputs = inputElements
			.map(tdInput => {
				const inputObject = tdInput.dataset.inputAttributes ? JSON.parse(tdInput.dataset.inputAttributes) : {}
				inputObject[tdInput.dataset.inputName] = tdInput.innerText
				return inputObject
			})

		return inputs
	}

	submit(event) {
		event.preventDefault()
		document.querySelectorAll('.disable-on-submit').forEach(element => element.classList.add('disabled'))

		fetch(this.element.action, {
			method: this.element.method,
			headers: {
				'X-CSRF-Token': get_csrf_token(),
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(this.serialize())
		})
		.then(response => {
			document.querySelectorAll('.disable-on-submit').forEach(element => element.classList.remove('disabled'))
			return response.text()
		})
		.then(html => { Turbo.renderStreamMessage(html) })
		.catch(error => {
			console.error('Error:', error);
			alert("An error occurred while submitting the form.");
		});
	}

	submitForm() {
		const inputs = this.serialize()
		inputs.forEach(inputObject => this.addInputToForm(inputObject))
		Turbo.navigator.submitForm(this.formTarget)
		this.removeInputsFromForm(inputs)
	}

	addInputToForm(inputObject) {
		for (const key in inputObject) {
			const inputElement = document.createElement('input')
			inputElement.type  = 'hidden'
			inputElement.name  = key
			inputElement.value = inputObject[key]

			this.formTarget.appendChild(inputElement)
		}
	}

	removeInputsFromForm(inputObject) {
		const inputNamesToRemove = new Set(inputObject.flatMap(input => input.keys))

		inputNamesToRemove.forEach(inputName => {
			this.formTarget.querySelectorAll(`input[name=${inputName}]`)
				.forEach(inputElement => inputElement.remove())
		})
	}

}
